'use client';

import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function CookiePolicyPage() {
  return (
    <>
      <Header />
      <main className="bg-gray-950 min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Cookie Policy</h1>
            <p className="text-gray-400 leading-relaxed">
              This Cookie Policy explains how E-GREGOR GROUP LTD uses cookies and similar tracking
              technologies when you visit our website at{' '}
              <a href="https://egregor.info" className="text-blue-400 hover:underline">
                egregor.info
              </a>
              .
            </p>
            <p className="text-gray-500 mt-3 text-sm">
              This Cookie Policy was last updated on April 1, 2026.
            </p>
          </div>

          <div className="space-y-10 text-gray-300">
            {/* What Are Cookies */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">What Are Cookies?</h2>
              <p className="leading-relaxed mb-3">
                Cookies are small text files that are placed on your device (computer, smartphone, or
                tablet) when you visit a website. They are widely used to make websites work more
                efficiently, to provide a better user experience, and to give website owners information
                about how their site is being used.
              </p>
              <p className="leading-relaxed">
                Cookies may be set by the website you are visiting (&quot;first-party cookies&quot;) or by
                third-party services operating on that website (&quot;third-party cookies&quot;). They can be
                &quot;session cookies&quot; (deleted when you close your browser) or &quot;persistent cookies&quot;
                (which remain on your device for a set period or until you delete them).
              </p>
            </section>

            {/* How We Use Cookies */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">How We Use Cookies</h2>
              <p className="leading-relaxed mb-4">
                We use cookies for the following purposes:
              </p>

              <div className="mb-5">
                <h3 className="text-white font-medium mb-2">Strictly Necessary Cookies</h3>
                <p className="text-gray-400 leading-relaxed mb-2">
                  These cookies are essential for the website to function properly. They enable core
                  functionality such as security, network management, and accessibility. You cannot
                  opt out of these cookies as the website cannot function without them.
                </p>
                <ul className="list-disc list-inside space-y-1 pl-2 text-gray-400">
                  <li>Session management and authentication</li>
                  <li>Security and fraud prevention</li>
                  <li>Load balancing and performance</li>
                </ul>
              </div>

              <div className="mb-5">
                <h3 className="text-white font-medium mb-2">Performance &amp; Analytics Cookies</h3>
                <p className="text-gray-400 leading-relaxed mb-2">
                  These cookies collect information about how visitors use our website, such as which
                  pages are visited most often and whether visitors receive error messages. All
                  information collected is aggregated and anonymous.
                </p>
                <ul className="list-disc list-inside space-y-1 pl-2 text-gray-400">
                  <li>Google Analytics — tracks page views, session duration, and user behaviour</li>
                  <li>Performance monitoring tools to identify and resolve technical issues</li>
                </ul>
              </div>

              <div className="mb-5">
                <h3 className="text-white font-medium mb-2">Functional Cookies</h3>
                <p className="text-gray-400 leading-relaxed mb-2">
                  These cookies allow the website to remember choices you make (such as your language
                  preference or region) and provide enhanced, more personalised features.
                </p>
                <ul className="list-disc list-inside space-y-1 pl-2 text-gray-400">
                  <li>Remembering your preferences and settings</li>
                  <li>Storing your consent choices</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-medium mb-2">Marketing &amp; Targeting Cookies</h3>
                <p className="text-gray-400 leading-relaxed mb-2">
                  These cookies may be set through our website by our advertising partners to build a
                  profile of your interests and show you relevant adverts on other sites. They do not
                  store directly personal information but are based on uniquely identifying your browser
                  and internet device.
                </p>
                <ul className="list-disc list-inside space-y-1 pl-2 text-gray-400">
                  <li>Google Ads remarketing</li>
                  <li>Social media tracking pixels (LinkedIn, Twitter/X)</li>
                </ul>
              </div>
            </section>

            {/* Third-Party Cookies */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Third-Party Cookies</h2>
              <p className="leading-relaxed mb-3">
                Some cookies on our website are set by third-party services. We do not control these
                cookies and they are subject to the respective third party&apos;s own privacy and cookie
                policies. Third-party services we use may include:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2 text-gray-400">
                <li>
                  <span className="text-white font-medium">Google Analytics</span> — website analytics
                  and performance measurement (
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline"
                  >
                    Google Privacy Policy
                  </a>
                  )
                </li>
                <li>
                  <span className="text-white font-medium">Google Ads</span> — advertising and
                  remarketing services
                </li>
                <li>
                  <span className="text-white font-medium">LinkedIn</span> — professional network
                  tracking and advertising
                </li>
              </ul>
            </section>

            {/* Your Cookie Choices */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Your Cookie Choices</h2>
              <p className="leading-relaxed mb-3">
                In accordance with the{' '}
                <strong className="text-white">UK Privacy and Electronic Communications Regulations (PECR)</strong>{' '}
                and the{' '}
                <strong className="text-white">UK GDPR</strong>, we will ask for your consent before
                placing non-essential cookies on your device.
              </p>
              <p className="leading-relaxed mb-3">
                You can manage or withdraw your consent at any time. You also have the right to control
                cookies through your browser settings:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2 text-gray-400">
                <li>
                  <span className="text-white font-medium">Browser settings</span> — most browsers
                  allow you to refuse or delete cookies via their settings menu. Please refer to your
                  browser&apos;s help documentation for instructions.
                </li>
                <li>
                  <span className="text-white font-medium">Opt-out tools</span> — you can opt out of
                  Google Analytics tracking at{' '}
                  <a
                    href="https://tools.google.com/dlpage/gaoptout"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline"
                  >
                    tools.google.com/dlpage/gaoptout
                  </a>
                  .
                </li>
              </ul>
              <p className="leading-relaxed mt-3 text-gray-400">
                Please note that disabling certain cookies may affect the functionality of our website
                and your user experience.
              </p>
            </section>

            {/* Retention Periods */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Cookie Retention Periods</h2>
              <p className="leading-relaxed mb-3">
                Different cookies are retained for different periods of time:
              </p>
              <ul className="space-y-3 pl-2 text-gray-400">
                <li>
                  <span className="text-white font-semibold">Session cookies</span> — deleted
                  automatically when you close your browser.
                </li>
                <li>
                  <span className="text-white font-semibold">Persistent cookies</span> — remain on
                  your device for a defined period (typically between 30 days and 2 years) or until
                  you delete them manually.
                </li>
                <li>
                  <span className="text-white font-semibold">Analytics cookies</span> — Google
                  Analytics cookies are retained for up to 26 months.
                </li>
              </ul>
            </section>

            {/* Legal Basis */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Legal Basis for Using Cookies</h2>
              <p className="leading-relaxed mb-3">
                We rely on the following legal bases for processing data via cookies:
              </p>
              <ul className="space-y-3 pl-2 text-gray-400">
                <li>
                  <span className="text-white font-semibold">Consent</span> — for non-essential
                  cookies (analytics, marketing, functional), we rely on your freely given, specific,
                  and informed consent under UK GDPR Article 6(1)(a).
                </li>
                <li>
                  <span className="text-white font-semibold">Legitimate interests</span> — for
                  strictly necessary cookies required for the secure and efficient operation of our
                  website under UK GDPR Article 6(1)(f).
                </li>
              </ul>
            </section>

            {/* Changes to This Policy */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Changes to This Cookie Policy</h2>
              <p className="leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in technology,
                legislation, or our data practices. We will notify you of any significant changes by
                updating the &quot;last updated&quot; date at the top of this page. We encourage you to review
                this policy periodically.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Contact Us</h2>
              <p className="leading-relaxed mb-3">
                If you have any questions about our use of cookies or this Cookie Policy, please
                contact us:
              </p>
              <ul className="space-y-1 pl-4 text-gray-400">
                <li>
                  Email:{' '}
                  <a href="mailto:sales@egregor.cc" className="text-blue-400 hover:underline">
                    sales@egregor.cc
                  </a>
                </li>
                <li>Post: BUNYAMIN CELEBE — CEO</li>
                <li>E-GREGOR GROUP LTD</li>
                <li>20 Wenlock Road, London, England, N1 7GU</li>
              </ul>
              <p className="leading-relaxed mt-4 text-gray-400">
                You also have the right to lodge a complaint with the{' '}
                <a
                  href="https://ico.org.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  Information Commissioner&apos;s Office (ICO)
                </a>{' '}
                if you believe your data rights have been infringed.
              </p>
            </section>
          </div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <Link href="/" className="text-blue-400 hover:text-blue-300 transition-colors text-sm">
              ← Back to Home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
